import React from "react";
import { ICommand } from "Application";
import { Box, Show } from "Components";
import { CellContainer } from "./ScheduleChartCell.styles";

import { MentorAvailability, CellRenderState } from "../../Types";
import { ActionButton } from "./ActionButton";
import { StudentNameLabel, TimesLabel, SessionStatusLabel, AttendanceLabel, IncidentLabel, CrossIcon, PlusIcon, MinusIcon } from "./ScheduleChartCell.styles";

export interface CellCommands {
    openStudentDetail: { command: ICommand; params: { mentorId: string; sessionKey: string; mentorStudentRotaId: string } };
    openAddStudent: { command: ICommand; params: { mentorId: string; sessionKey: string } };
    deleteStudentMentor: { command: ICommand; params: { mentorStudentRotaId: Guid; studentSessionId: Guid } };
    openOverTime: { command: ICommand; params: { mentorId: string; sessionKey: string } };
    deleteMentorAbsence: { command: ICommand; params: { key: string; startDate: any; mentor: MentorAvailability } };
    removeDetails: { command: ICommand; params: Guid };
}

interface ScheduleChartCellProps {
    state: CellRenderState;
    isDeleting: boolean;
    deleteDisabled: boolean;
    commands: CellCommands;
}

export const ScheduleChartCell: React.FC<ScheduleChartCellProps> = ({ state, isDeleting, deleteDisabled, commands }) => (
    <CellContainer bg={state.background}>
        <UnavailableAssignedName state={state} commands={commands} />
        <RemoveDetails state={state} commands={commands} />
        <CrossIconAction state={state} commands={commands} />
        <OverTimeAction state={state} commands={commands} />
        <UnavailableDeleteAction state={state} isDeleting={isDeleting} deleteDisabled={deleteDisabled} commands={commands} />
        <SessionContent state={state} isDeleting={isDeleting} deleteDisabled={deleteDisabled} commands={commands} />
    </CellContainer>
);

const UnavailableAssignedName: React.FC<{ state: CellRenderState; commands: CellCommands }> = ({ state, commands }) => (
    <Show if={state.showEditUnavailableAssigned}>
        <StudentNameLabel command={commands.openStudentDetail.command} commandParam={commands.openStudentDetail.params}>
            {state.studentName}
        </StudentNameLabel>
    </Show>
);

const RemoveDetails: React.FC<{ state: CellRenderState; commands: CellCommands }> = ({ state, commands }) => (
    <Show if={state.showRemoveDetailsButton}>
        <ActionButton command={commands.removeDetails.command} commandParam={commands.removeDetails.params} className="crossImage">
            <CrossIcon />
        </ActionButton>
    </Show>
);

const CrossIconAction: React.FC<{ state: CellRenderState; commands: CellCommands }> = ({ state, commands }) => (
    <Show if={state.showCrossIconAction}>
        <ActionButton command={commands.deleteMentorAbsence.command} commandParam={commands.deleteMentorAbsence.params} className="crossBtn">
            <CrossIcon />
        </ActionButton>
    </Show>
);

const OverTimeAction: React.FC<{ state: CellRenderState; commands: CellCommands }> = ({ state, commands }) => (
    <Show if={state.showOverTimeAction}>
        <ActionButton command={commands.openOverTime.command} commandParam={commands.openOverTime.params}>
            <PlusIcon />
        </ActionButton>
    </Show>
);

const UnavailableDeleteAction: React.FC<ScheduleChartCellProps> = ({ state, isDeleting, deleteDisabled, commands }) => (
    <Show if={state.showUnavailableDeleteAction}>
        <ActionButton command={commands.deleteStudentMentor.command} commandParam={commands.deleteStudentMentor.params} loading={isDeleting} disabled={deleteDisabled}>
            <MinusIcon />
        </ActionButton>
    </Show>
);

const SessionContent: React.FC<ScheduleChartCellProps> = ({ state, isDeleting, deleteDisabled, commands }) => (
    <Show if={state.showSessionContent}>
        <Box display={"flex"} flexDirection={"column"}>
            <LiveModeStatus state={state} />
            <Show if={state.showStudentNameLink}>
                <StudentNameLabel command={commands.openStudentDetail.command} commandParam={commands.openStudentDetail.params}>
                    {state.studentName}
                </StudentNameLabel>
            </Show>
            <Show if={state.showTimes}>
                <TimesLabel>{state.formattedTimeRange}</TimesLabel>
            </Show>
            <Show if={state.showAddStudentButton}>
                <ActionButton command={commands.openAddStudent.command} commandParam={commands.openAddStudent.params}>
                    <PlusIcon />
                </ActionButton>
            </Show>
            <Show if={state.showEditDeleteButton}>
                <ActionButton command={commands.deleteStudentMentor.command} commandParam={commands.deleteStudentMentor.params} loading={isDeleting} disabled={deleteDisabled}>
                    <MinusIcon />
                </ActionButton>
            </Show>
        </Box>
    </Show>
);

const LiveModeStatus: React.FC<{ state: CellRenderState }> = ({ state }) => (
    <Show if={state.showLiveModeStatus}>
        <>
            <SessionStatusLabel fontStyle="h4" $color={state.sessionStatusColour}>
                {state.sessionStatusText}
            </SessionStatusLabel>
            <AttendanceLabel fontStyle="h4" $color={state.attendanceColour}>
                {state.attendanceText}
            </AttendanceLabel>
            <IncidentLabel fontStyle="h6">{state.incidentText}</IncidentLabel>
        </>
    </Show>
);
